import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { api } from '../api/client';
import type { JournalEntry } from '../api/types';
import { AccountDeletion } from '../components/AccountDeletion';
import { useLeaveWarning } from '../hooks/useLeaveWarning';
import { formatDate, formatNumber, useJourneyLocale } from '../i18n/journey';
import { readJournalDrafts, writeJournalEditDraft, writeNewJournalDraft } from '../state/journalDraft';
import { useUser } from '../state/UserContext';

const MAX_TEXT_LENGTH = 20_000;
const PAGE_SIZE = 20;

export function Journal() {
  const { me } = useUser();
  const { t, lang } = useJourneyLocale();
  const owner = me?.sub ?? '';
  const restored = useMemo(() => readJournalDrafts(owner), [owner]);
  const restoredEditId = Object.keys(restored.edits)[0] ?? null;

  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState(restored.newDraft);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(restoredEditId);
  const [editingText, setEditingText] = useState(restoredEditId ? restored.edits[restoredEditId] : '');
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const formatTimestamp = (iso: string) => formatDate(lang, new Date(iso), { dateStyle: 'medium', timeStyle: 'short' });

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .listJournalEntries({ limit: PAGE_SIZE })
      .then((res) => {
        setEntries(res.entries);
        setNextCursor(res.nextCursor ?? null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : t('journal.loadFailed')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await api.listJournalEntries({ cursor: nextCursor, limit: PAGE_SIZE });
      setEntries((prev) => {
        const seen = new Set(prev.map((e) => e.entryId));
        return [...prev, ...res.entries.filter((e) => !seen.has(e.entryId))];
      });
      setNextCursor(res.nextCursor ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('journal.loadMoreFailed'));
    } finally {
      setLoadingMore(false);
    }
  };

  // Unsaved writing = a non-empty new draft, or an open edit whose text differs from what's saved.
  const editingOriginal = editingId ? entries.find((e) => e.entryId === editingId)?.text : undefined;
  const hasUnsavedEdit = editingId !== null && editingOriginal !== undefined && editingText !== editingOriginal;
  const hasUnsaved = draft.trim() !== '' || hasUnsavedEdit;
  useLeaveWarning(hasUnsaved, t('journal.leaveWarning'));

  const updateDraft = (text: string) => {
    setDraft(text);
    writeNewJournalDraft(owner, text);
  };

  const updateEditingText = (entryId: string, text: string) => {
    setEditingText(text);
    writeJournalEditDraft(owner, entryId, text);
  };

  const handleCreate = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const entry = await api.createJournalEntry({ text: draft });
      setEntries((prev) => [entry, ...prev]);
      updateDraft('');
    } catch (e) {
      // The draft is kept (in the box and in memory) -- a failed save never discards writing.
      setError(e instanceof Error ? t('journal.notSaved', { message: e.message }) : t('journal.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (entry: JournalEntry) => {
    if (editingId && editingId !== entry.entryId && hasUnsavedEdit && !window.confirm(t('journal.confirmDiscardOther'))) {
      return;
    }
    if (editingId) writeJournalEditDraft(owner, editingId, null);
    setConfirmDeleteId(null);
    setEditingId(entry.entryId);
    setEditingText(entry.text);
  };

  const cancelEditing = () => {
    if (hasUnsavedEdit && !window.confirm(t('journal.confirmDiscard'))) return;
    if (editingId) writeJournalEditDraft(owner, editingId, null);
    setEditingId(null);
    setEditingText('');
  };

  const handleSaveEdit = async (entryId: string) => {
    if (!editingText.trim()) return;
    setSavingEdit(true);
    setError(null);
    try {
      const updated = await api.updateJournalEntry(entryId, { text: editingText });
      setEntries((prev) => prev.map((e) => (e.entryId === entryId ? updated : e)));
      writeJournalEditDraft(owner, entryId, null);
      setEditingId(null);
      setEditingText('');
    } catch (e) {
      setError(e instanceof Error ? t('journal.changesNotSaved', { message: e.message }) : t('journal.saveEditFailed'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (entryId: string) => {
    setError(null);
    setDeletingId(entryId);
    try {
      await api.deleteJournalEntry(entryId);
      setEntries((prev) => prev.filter((e) => e.entryId !== entryId));
      setConfirmDeleteId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('journal.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async () => {
    setError(null);
    setExporting(true);
    setExportStatus(t('journal.exportPreparing'));
    try {
      // Follows every export chunk until the server reports there's nothing more -- a large
      // journal arrives in several bounded responses rather than one that could be truncated.
      const all = await api.exportAllJournalEntries((count) =>
        setExportStatus(t('journal.exportProgress', { count, n: formatNumber(lang, count) })),
      );
      const blob = new Blob([JSON.stringify(all, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `omnist-journal-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setExportStatus(t('journal.exported', { count: all.length, n: formatNumber(lang, all.length) }));
    } catch (e) {
      setExportStatus(null);
      setError(e instanceof Error ? t('journal.exportFailed', { message: e.message }) : t('journal.exportFailedGeneric'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">{t('journal.title')}</h1>
      <p className="page-subtitle">{t('journal.subtitle')}</p>

      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}

      {/* Wide screens: writing (and the data/privacy notes) on the left, entries on the right.
          Phones keep the original order: write, read, then data. */}
      <div className="journal-layout">
        <div className="journal-compose">
          <div className="themed-card journal-compose-card">
            <label htmlFor="journal-new-entry" className="visually-hidden">
              {t('journal.newEntryLabel')}
            </label>
            <textarea
              id="journal-new-entry"
              className="journal-textarea"
              value={draft}
              onChange={(e) => updateDraft(e.target.value)}
              placeholder={t('journal.placeholder')}
              maxLength={MAX_TEXT_LENGTH}
              rows={4}
            />
            {draft.trim() !== '' && <p className="journal-draft-note">{t('journal.draftNote')}</p>}
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={handleCreate}
              disabled={saving || !draft.trim()}
              style={{ marginTop: 10 }}
            >
              {saving ? t('journal.saving') : t('journal.addEntry')}
            </button>
          </div>
        </div>

        <section className="journal-entries" aria-labelledby="journal-entries-heading">
          <h2 id="journal-entries-heading" className="section-title journal-entries-heading">
            {t('journal.entriesHeading')}
          </h2>
          {loading ? (
            <div className="center-page" style={{ minHeight: '30vh' }}>
              <div className="spinner" />
            </div>
          ) : entries.length === 0 && error ? (
            // A failed load is not an empty journal: keep the error (shown above) and offer a retry.
            <div className="empty-state">
              <button type="button" className="btn btn-secondary" onClick={load}>
                {t('shared.tryAgain')}
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div className="empty-state">{t('journal.empty')}</div>
          ) : (
            <div className="journal-list">
              {entries.map((entry) => (
                <div key={entry.entryId} className="journal-entry-card">
                  <div className="journal-entry-meta">{formatTimestamp(entry.createdAt)}</div>
                  {editingId === entry.entryId ? (
                    <>
                      <label htmlFor={`journal-edit-${entry.entryId}`} className="visually-hidden">
                        {t('journal.editLabel')}
                      </label>
                      <textarea
                        id={`journal-edit-${entry.entryId}`}
                        className="journal-textarea"
                        value={editingText}
                        onChange={(e) => updateEditingText(entry.entryId, e.target.value)}
                        maxLength={MAX_TEXT_LENGTH}
                        rows={4}
                      />
                      {hasUnsavedEdit && <p className="journal-draft-note">{t('journal.editNotSaved')}</p>}
                      <div className="journal-entry-actions">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => handleSaveEdit(entry.entryId)}
                          disabled={savingEdit || !editingText.trim()}
                        >
                          {savingEdit ? t('journal.saving') : t('journal.save')}
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={cancelEditing} disabled={savingEdit}>
                          {t('common.cancel')}
                        </button>
                      </div>
                    </>
                  ) : confirmDeleteId === entry.entryId ? (
                    <>
                      <p className="journal-entry-text">{entry.text}</p>
                      <div className="journal-delete-confirm" role="group" aria-label={t('journal.deleteConfirmAria')}>
                        <p>{t('journal.deleteConfirm')}</p>
                        <div className="journal-entry-actions">
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => handleDelete(entry.entryId)}
                            disabled={deletingId === entry.entryId}
                          >
                            {deletingId === entry.entryId ? t('journal.deleting') : t('journal.deletePermanently')}
                          </button>
                          <button type="button" className="btn btn-ghost" onClick={() => setConfirmDeleteId(null)}>
                            {t('journal.keepEntry')}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="journal-entry-text">{entry.text}</p>
                      <div className="journal-entry-actions">
                        <button type="button" className="btn btn-ghost" onClick={() => startEditing(entry)}>
                          {t('journal.edit')}
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={() => setConfirmDeleteId(entry.entryId)}>
                          {t('journal.delete')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {nextCursor && (
                <button type="button" className="btn btn-secondary btn-block" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? t('journal.loadingMore') : t('journal.showOlder')}
                </button>
              )}
            </div>
          )}
        </section>

        <section className="journal-data-section" aria-labelledby="journal-data-heading">
          <h2 id="journal-data-heading" className="section-title">{t('journal.dataHeading')}</h2>
          <ul className="journal-privacy-list">
            <li>
              <Trans i18nKey="journal.privacy.storage" components={{ strong: <strong /> }} />
            </li>
            <li>{t('journal.privacy.noAnalytics')}</li>
            <li>{t('journal.privacy.drafts')}</li>
            <li>{t('journal.privacy.deletion')}</li>
          </ul>

          <button type="button" className="btn btn-secondary" onClick={handleExport} disabled={exporting}>
            {exporting ? t('journal.exporting') : t('journal.exportButton')}
          </button>
          {exportStatus && (
            <p className="page-subtitle" role="status" style={{ marginTop: 8 }}>
              {exportStatus}
            </p>
          )}

          <div className="account-delete-block">
            <AccountDeletion />
          </div>
        </section>
      </div>
    </div>
  );
}
